/**
 * Warehouse address lookup for the contact picker. Lists `/warehouse/addresses`
 * and adapts a saved address into a ContactPicker-compatible shape so a stored
 * address can stand in for a contact.
 *
 * Contains:
 * - fetchAddressesAPI: query-param address list
 * - addressToFakeContact: address to picker-contact adapter
 */
import apiClient from '@sophocles/http-common';

const ADDRESSES_ENDPOINT = '/warehouse/addresses';

/**
 * Fetch addresses for a customer or site group
 *
 * @param {Object} filters - { customer, site_group, primary_only, is_billing, include_customer_addresses }
 * @returns {Promise<Array>} - Address records
 */
export const fetchAddressesAPI = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.customer) params.append('customer', filters.customer);
  if (filters.site_group) params.append('site_group', filters.site_group);
  if (filters.primary_only !== undefined) params.append('primary_address', filters.primary_only);
  if (filters.is_billing !== undefined) params.append('is_billing', filters.is_billing);
  if (filters.include_customer_addresses !== undefined)
    params.append('include_customer_addresses', filters.include_customer_addresses);

  const response = await apiClient.get(`${ADDRESSES_ENDPOINT}/?${params.toString()}`);
  return response.data.results || response.data;
};

/**
 * Convert Address record to Contact-like format for ContactPicker
 *
 * This creates a "fake contact" object that looks like a Contact record
 * but represents a company default address (no person name).
 *
 * @param {Object} address - Address model record
 * @param {string} businessName - Customer or SiteGroup name to display
 * @returns {Object} - Fake contact object compatible with ContactPicker
 */
export const addressToFakeContact = (address, businessName) => {
  // Build full address display string
  const addressParts = [
    address.address_1,
    address.address_2,
    address.city,
    address.state && address.zipcode ? `${address.state} ${address.zipcode}` : address.state || address.zipcode,
  ].filter(Boolean);

  const full_address_display = addressParts.join(', ');

  return {
    id: `address-${address.id}`, // Prefix to distinguish from Contact IDs
    _isAddress: true, // Flag for frontend logic
    _addressId: address.id, // Original Address ID
    name: '', // No person name for Address records
    business_name: businessName,
    email: '',
    phone1: '',
    address: address.address_1,
    address_2: address.address_2,
    city: address.city,
    state: address.state,
    zipcode: address.zipcode,
    country: address.country,
    full_address_display,
    // Don't inherit primary flags - these indicate whether the Contact is primary,
    // not whether the source Address was. When using address as inline data,
    // user can choose to make it primary later when saving as a new contact.
    is_primary_rma_billing: false,
    is_primary_rma_shipping: false,
    is_primary_sales_billing: false,
    is_primary_sales_shipping: false,
    // Preserve original address metadata for reference
    _sourceIsPrimaryAddress: address.primary_address,
    _sourceIsBillingAddress: address.is_billing,
  };
};
